import React, { useState, useEffect } from 'react';
import { SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions, ScrollView, RefreshControl, StatusBar, Alert, Modal, FlatList, ActivityIndicator, TextInput, Switch, Image, KeyboardAvoidingView, Platform } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  jobs: `${API_BASE_URL}/api/data/jobs`,
  applications: `${API_BASE_URL}/api/data/applications`,
  employees: `${API_BASE_URL}/api/data/employees`,
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);
const safeData=r=>(r?.data?.success&&Array.isArray(r.data.data))?r.data.data:[];

export default function EditJobScreen({ navigation, route }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [companyId, setCompanyId] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(route?.params?.companyProfile || null);
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobForm, setJobForm] = useState({title:'',description:'',location:'',salary_min:'',salary_max:'',type:'full-time',department:'',experience:'',status:'active'});

  // Same logo loading as EditSupplierScreen
  useFocusEffect(
    React.useCallback(() => {
      loadCompanyLogo();
    }, [])
  );

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
        if (profile.name || profile.company_name) setCompanyName(profile.name || profile.company_name);
        setCompanyProfile(profile);
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (cid) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: cid },
        });
        if (response.data?.success && response.data.data?.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          if (latestProfile.name || latestProfile.company_name) setCompanyName(latestProfile.name || latestProfile.company_name);
          setCompanyProfile(latestProfile);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  useEffect(()=>{init();},[]);
  const init=async()=>{try{setIsLoading(true);const b=route?.params?.company_id||await AsyncStorage.getItem('company_id')||'0';setCompanyId(b);await fetchAll(b);}catch(e){console.error(e);}finally{setIsLoading(false);}};
  const onRefresh=async()=>{setRefreshing(true);await init();setRefreshing(false);};

  const fetchAll=async(bizId)=>{
    const cid=parseInt(bizId)||0;const p={company_id:cid,limit:500};
    const [r0,r1,r2]=await Promise.allSettled([
      api.get(API_ENDPOINTS.jobs,{params:p}).catch(()=>({data:{success:false}})),
      api.get(API_ENDPOINTS.applications,{params:p}).catch(()=>({data:{success:false}})),
      api.get(API_ENDPOINTS.employees,{params:p}).catch(()=>({data:{success:false}}))
    ]);
    const jobs=safeData(r0.status==="fulfilled"?r0.value:null);
    const apps=safeData(r1.status==="fulfilled"?r1.value:null);
    const employees=safeData(r2.status==="fulfilled"?r2.value:null);
    const jobId=route?.params?.jobId||route?.params?.job_id;
    const job=jobId?jobs.find(j=>String(j.id)===String(jobId)):jobs[0];
    if(job){setSelectedJob(job);setJobForm({title:job.title||'',description:job.description||'',location:job.location||'',salary_min:String(job.salary_min||''),salary_max:String(job.salary_max||''),type:job.employment_type||job.type||'full-time',department:job.department||'',experience:job.experience_level||'',status:job.status||'active'});}
    setItems(jobs);setStats({totalJobs:jobs.length,applications:apps.length,employees:employees.length});
  };

  const formatCurrency=v=>new Intl.NumberFormat('en-GB',{style:'currency',currency:companyProfile?.currency||'GBP',minimumFractionDigits:0,maximumFractionDigits:0}).format(v||0);
  const formatDate=d=>d?new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}):'';
  const filtered=items.filter(i=>{if(!searchQuery)return true;return JSON.stringify(i).toLowerCase().includes(searchQuery.toLowerCase());});

  const renderHeader=()=>(
    <LinearGradient colors={['#1E40AF','#3B82F6']} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity onPress={()=>navigation?.goBack()} style={st.hBtn}><MaterialCommunityIcons name="arrow-left" size={24} color="#FFF"/></TouchableOpacity>
        <View style={{flexDirection:'row',alignItems:'center',flex:1,marginLeft:12}}>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={{width:30,height:30,borderRadius:15,marginRight:10}} resizeMode="cover"/>
          ) : (
            <MaterialCommunityIcons name="office-building" size={30} color="#FFF" style={{marginRight:10}}/>
          )}
          <View><Text style={st.hTitle}>{companyName||companyProfile?.name||'Edit Job'}</Text>
            <Text style={[st.hSub,{color:'#BFDBFE'}]}>{companyProfile?.industry?`${companyProfile.industry} · `:''}Edit Job</Text></View>
        </View>
        <TouchableOpacity style={st.hBtn} onPress={()=>handleSaveJob()}><MaterialCommunityIcons name="content-save" size={24} color="#FFF"/></TouchableOpacity>
      </View>
      {companyProfile&&<View style={st.chips}>
        {companyProfile.headquarters&&<View style={st.chip}><MaterialCommunityIcons name="map-marker" size={12} color="#BFDBFE"/><Text style={[st.chipTxt,{color:'#BFDBFE'}]}>{companyProfile.headquarters}</Text></View>}
        {companyProfile.employee_count&&<View style={st.chip}><MaterialCommunityIcons name="account-group" size={12} color="#BFDBFE"/><Text style={[st.chipTxt,{color:'#BFDBFE'}]}>{companyProfile.employee_count} team</Text></View>}
      </View>}
    </LinearGradient>
  );

  const renderStats=()=>(
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.statsBar} contentContainerStyle={{paddingHorizontal:16}}>
      {Object.entries(stats).slice(0,7).map(([k,v],i)=>(
        <View key={i} style={st.statCard}><Text style={[st.statVal,{color:PRIMARY}]}>{typeof v==='number'&&v>999?formatCurrency(v):typeof v==='number'?v%1!==0?v.toFixed(1):String(v):String(v)}</Text><Text style={st.statLbl}>{k.replace(/([A-Z])/g,' $1').replace(/^./,s=>s.toUpperCase())}</Text></View>
      ))}
    </ScrollView>
  );

  const renderTabs=()=>(
    <View style={st.tabsRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:12}}>
      {[{k:'details',t:'Details',i:'file-document-edit'},{k:'requirements',t:'Requirements',i:'clipboard-list'},{k:'preview',t:'Preview',i:'eye'}].map(tab=>(
        <TouchableOpacity key={tab.k} style={[st.tab,activeTab===tab.k&&st.activeTab]} onPress={()=>setActiveTab(tab.k)}>
          <MaterialCommunityIcons name={tab.i} size={18} color={activeTab===tab.k?PRIMARY:'#6B7280'}/>
          <Text style={[st.tabTxt,activeTab===tab.k&&{color:PRIMARY,fontWeight:'600'}]}>{tab.t}</Text></TouchableOpacity>
      ))}
    </ScrollView></View>
  );

  const renderItems=()=>(
    <View>
      <View style={st.searchRow}><MaterialCommunityIcons name="magnify" size={20} color="#9CA3AF"/>
        <TextInput style={st.searchInput} placeholder="Search..." placeholderTextColor="#9CA3AF" value={searchQuery} onChangeText={setSearchQuery}/></View>
      {filtered.length===0?<View style={st.empty}><MaterialCommunityIcons name="magnify" size={48} color="#D1D5DB"/><Text style={st.emptyTxt}>No items found</Text></View>:
        filtered.slice(0,50).map((item,i)=>(
          <TouchableOpacity key={i} style={st.card} onPress={()=>{setSelected(item);setShowModal(true);}}>
            <View style={{flexDirection:'row',alignItems:'center'}}>
              <View style={[st.avatar,{backgroundColor:PRIMARY}]}><Text style={st.avatarTxt}>{(item.name||item.title||item.contact_name||item.company||item.company_name||item.candidate_name||'?').substring(0,2).toUpperCase()}</Text></View>
              <View style={{flex:1}}>
                <Text style={st.cardName}>{item.name||item.title||item.contact_name||item.company_name||item.candidate_name||item.invoice_number||`#${item.id}`}</Text>
                <Text style={st.cardMeta}>{item.type||item.status||item.stage||item.email||item.company||item.department||item.description?.substring(0,60)||''}</Text>
              </View>
              {parseFloat(item.value||item.total||item.revenue||item.salary_max||0)>0&&<Text style={st.cardVal}>{formatCurrency(item.value||item.total||item.revenue||item.salary_max)}</Text>}
            </View>
            {item.status&&<View style={[st.badge,{backgroundColor:(item.status==='active'||item.status==='completed'||item.status==='paid'||item.status==='hired'?'#10B981':item.status==='pending'||item.status==='new'?'#F59E0B':'#6B7280')+'20'}]}><Text style={[st.badgeTxt,{color:item.status==='active'||item.status==='completed'||item.status==='paid'||item.status==='hired'?'#10B981':item.status==='pending'||item.status==='new'?'#F59E0B':'#6B7280'}]}>{item.status}</Text></View>}
          </TouchableOpacity>
        ))
      }
    </View>
  );

  const renderAnalytics=()=>(
    <View style={st.section}><Text style={st.secTitle}>Analytics</Text>
      <View style={st.grid}>{Object.entries(stats).map(([k,v],i)=>(
        <View key={i} style={st.gridCard}><Text style={[st.gridVal,{color:PRIMARY}]}>{typeof v==='number'&&v>999?formatCurrency(v):typeof v==='number'?v%1!==0?v.toFixed(1):String(v):String(v)}</Text><Text style={st.gridLbl}>{k.replace(/([A-Z])/g,' $1').replace(/^./,s=>s.toUpperCase())}</Text></View>
      ))}</View>
    </View>
  );

  const handleSaveJob=async()=>{
    if(!jobForm.title){Alert.alert('Error','Job title is required');return;}
    try{
      const cid=parseInt(companyId)||0;
      const payload={
        title:jobForm.title,department:jobForm.department||'',location:jobForm.location||'',
        job_type:jobForm.job_type||jobForm.employment_type||'Full-time',employment_type:jobForm.employment_type||jobForm.job_type||'Full-time',
        experience_level:jobForm.experience_level||'',description:jobForm.description||'',
        responsibilities:jobForm.responsibilities?String(jobForm.responsibilities).split('\n').filter(r=>r.trim()):[],
        qualifications:jobForm.requirements?String(jobForm.requirements).split('\n').filter(r=>r.trim()):[],
        required_skills:Array.isArray(jobForm.required_skills)?jobForm.required_skills:[],
        salary_min:parseFloat(jobForm.salary_min)||0,salary_max:parseFloat(jobForm.salary_max)||0,
        salary_currency:jobForm.salary_currency||'GBP',positions_available:parseInt(jobForm.positions_available)||1,
        application_deadline:jobForm.application_deadline||null,is_featured:jobForm.is_featured||false,
        is_active:jobForm.status==='active',status:jobForm.status||'active',company_id:cid,updated_at:new Date().toISOString()};
      if(!selectedJob?.id)payload.job_id='JOB-'+Date.now().toString(36).toUpperCase();
      if(selectedJob?.id){await api.put(API_ENDPOINTS.jobs+'/'+selectedJob.id,payload);Alert.alert('Success','Job updated');}
      else{payload.posted_date=new Date().toISOString();await api.post(API_ENDPOINTS.jobs,payload);Alert.alert('Success','Job created');}
      const savedJobs=JSON.parse(await AsyncStorage.getItem('jobs')||'[]');
      if(selectedJob?.id){const idx=savedJobs.findIndex(j=>String(j.id)===String(selectedJob.id));if(idx>=0)savedJobs[idx]={...savedJobs[idx],...payload};}
      else{savedJobs.unshift({...payload,id:'job_'+Date.now()});}
      await AsyncStorage.setItem('jobs',JSON.stringify(savedJobs));
    }catch(e){
      Alert.alert('Saved Locally','Will sync when online');
    }
  };

  const formField=(label,key,placeholder,multiline=false,kbType='default')=>(
    <View style={st.formField}><Text style={st.formLbl}>{label}</Text>
      <TextInput style={[st.formInput,multiline&&{height:100,textAlignVertical:'top'}]} placeholder={placeholder} placeholderTextColor="#9CA3AF"
        value={jobForm[key]||''} onChangeText={v=>setJobForm(p=>({...p,[key]:v}))} multiline={multiline} keyboardType={kbType}/></View>);

  const renderDetailsForm=()=>(<View style={{padding:16}}>
    <View style={{backgroundColor:'#FFF',borderRadius:12,padding:14,marginBottom:16,borderWidth:1,borderColor:'#E5E7EB',flexDirection:'row',alignItems:'center'}}>
      {companyLogo ? (
        <Image source={{uri:companyLogo}} style={{width:50,height:50,borderRadius:25,marginRight:12,borderWidth:2,borderColor:'#E5E7EB'}} resizeMode="cover"/>
      ) : (
        <View style={{width:50,height:50,borderRadius:25,backgroundColor:PRIMARY,marginRight:12,alignItems:'center',justifyContent:'center'}}>
          <MaterialCommunityIcons name="office-building" size={24} color="#FFF"/>
        </View>
      )}
      <View style={{flex:1}}>
        <Text style={{fontSize:16,fontWeight:'700',color:'#1F2937'}}>{companyName||companyProfile?.name||'Company'}</Text>
        <Text style={{fontSize:12,color:'#6B7280'}}>{companyProfile?.industry||''}{companyProfile?.headquarters?' · '+companyProfile.headquarters:''}</Text>
      </View>
    </View>
    {formField('Job Title *','title','e.g. Senior Software Engineer')}
    {formField('Department','department','e.g. Engineering')}
    {formField('Location','location','e.g. London, UK')}
    <View style={{flexDirection:'row'}}><View style={{flex:1,marginRight:8}}>{formField('Min Salary','salary_min','0',false,'numeric')}</View><View style={{flex:1,marginLeft:8}}>{formField('Max Salary','salary_max','0',false,'numeric')}</View></View>
    <View style={st.formField}><Text style={st.formLbl}>Type</Text><ScrollView horizontal showsHorizontalScrollIndicator={false}>
      {['full-time','part-time','contract','temporary','internship','remote'].map(t=>(<TouchableOpacity key={t} style={{paddingHorizontal:14,paddingVertical:8,borderRadius:20,marginRight:8,backgroundColor:jobForm.type===t?PRIMARY:'#F3F4F6'}} onPress={()=>setJobForm(p=>({...p,type:t}))}>
        <Text style={{fontSize:13,fontWeight:'600',color:jobForm.type===t?'#FFF':'#6B7280'}}>{t.replace('-',' ').replace(/\b\w/g,c=>c.toUpperCase())}</Text></TouchableOpacity>))}</ScrollView></View>
    {formField('Experience','experience','e.g. Mid-Senior Level')}
    {formField('Description *','description','Job description...',true)}
    <View style={st.formField}><Text style={st.formLbl}>Status</Text><View style={{flexDirection:'row'}}>
      {['active','paused','closed','draft'].map(s=>(<TouchableOpacity key={s} style={{paddingHorizontal:14,paddingVertical:8,borderRadius:20,marginRight:8,backgroundColor:jobForm.status===s?(s==='active'?'#10B981':s==='paused'?'#F59E0B':'#6B7280'):'#F3F4F6'}} onPress={()=>setJobForm(p=>({...p,status:s}))}>
        <Text style={{fontSize:13,fontWeight:'600',color:jobForm.status===s?'#FFF':'#6B7280'}}>{s.charAt(0).toUpperCase()+s.slice(1)}</Text></TouchableOpacity>))}</View></View>
    <TouchableOpacity style={st.submitBtn} onPress={handleSaveJob}><MaterialCommunityIcons name="content-save" size={20} color="#FFF"/><Text style={st.submitBtnTxt}>{selectedJob?'Update Job':'Create Job'}</Text></TouchableOpacity>
  </View>);

  const renderRequirements=()=>(<View style={{padding:16}}>
    <View style={st.formField}><Text style={st.formLbl}>Requirements</Text><TextInput style={[st.formInput,{height:120,textAlignVertical:'top'}]} placeholder="List requirements..." placeholderTextColor="#9CA3AF" value={jobForm.requirements||''} onChangeText={v=>setJobForm(p=>({...p,requirements:v}))} multiline/></View>
    <View style={st.formField}><Text style={st.formLbl}>Benefits</Text><TextInput style={[st.formInput,{height:120,textAlignVertical:'top'}]} placeholder="List benefits..." placeholderTextColor="#9CA3AF" value={jobForm.benefits||''} onChangeText={v=>setJobForm(p=>({...p,benefits:v}))} multiline/></View>
    <View style={st.formField}><Text style={st.formLbl}>Skills</Text><TextInput style={st.formInput} placeholder="React, Node.js..." placeholderTextColor="#9CA3AF" value={jobForm.skills||''} onChangeText={v=>setJobForm(p=>({...p,skills:v}))}/></View>
    <TouchableOpacity style={st.submitBtn} onPress={handleSaveJob}><MaterialCommunityIcons name="content-save" size={20} color="#FFF"/><Text style={st.submitBtnTxt}>Save</Text></TouchableOpacity>
  </View>);

  const renderPreview=()=>(<View style={{padding:16}}>
    <View style={{backgroundColor:'#FFF',borderRadius:12,borderWidth:1,borderColor:'#E5E7EB',overflow:'hidden'}}>
      <LinearGradient colors={['#1E40AF','#3B82F6']} style={{padding:20}}>
        <View style={{flexDirection:'row',alignItems:'center',marginBottom:14}}>
          {companyLogo ? (
            <Image source={{uri:companyLogo}} style={{width:56,height:56,borderRadius:12,marginRight:14,borderWidth:2,borderColor:'rgba(255,255,255,0.4)'}} resizeMode="cover"/>
          ) : (
            <View style={{width:56,height:56,borderRadius:12,backgroundColor:'rgba(255,255,255,0.2)',marginRight:14,alignItems:'center',justifyContent:'center',borderWidth:2,borderColor:'rgba(255,255,255,0.4)'}}>
              <MaterialCommunityIcons name="office-building" size={24} color="#FFF"/>
            </View>
          )}
          <View><Text style={{color:'#FFF',fontSize:18,fontWeight:'800'}}>{companyName||'Company'}</Text>
            <Text style={{color:'#BFDBFE',fontSize:13,marginTop:2}}>{companyProfile?.industry||''}</Text></View>
        </View>
        <Text style={{color:'#FFF',fontSize:24,fontWeight:'900'}}>{jobForm.title||'Job Title'}</Text>
      </LinearGradient>
      <View style={{padding:16}}>
        <View style={{flexDirection:'row',flexWrap:'wrap',marginBottom:12}}>
          {[{t:jobForm.department||'General',bg:'#DBEAFE',c:'#1E40AF'},{t:jobForm.location||'Remote',bg:'#D1FAE5',c:'#065F46'},{t:formatCurrency(jobForm.salary_min)+' - '+formatCurrency(jobForm.salary_max),bg:'#FEF3C7',c:'#92400E'},{t:(jobForm.type||'full-time').replace('-',' '),bg:'#F3E8FF',c:'#6B21A8'}].map((b,i)=>(
            <View key={i} style={{backgroundColor:b.bg,paddingHorizontal:10,paddingVertical:4,borderRadius:12,marginRight:6,marginBottom:4}}><Text style={{fontSize:12,fontWeight:'600',color:b.c}}>{b.t}</Text></View>))}
        </View>
        <Text style={{fontSize:15,fontWeight:'700',color:'#1F2937',marginBottom:6}}>Description</Text>
        <Text style={{fontSize:14,color:'#374151',lineHeight:22}}>{jobForm.description||'No description yet'}</Text>
      </View>
    </View>
  </View>);

  const renderContent=()=>{
    const wrap=ch=><ScrollView style={{flex:1}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]}/>} showsVerticalScrollIndicator={false}>{ch}<View style={{height:40}}/></ScrollView>;
    if(activeTab==='details')return wrap(renderDetailsForm());
    if(activeTab==='requirements')return wrap(renderRequirements());
    if(activeTab==='preview')return wrap(renderPreview());
    if(activeTab==='analytics')return wrap(renderAnalytics());
    return wrap(renderDetailsForm());
  };

  const renderModal=()=>(
    <Modal visible={showModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowModal(false)}>
      <View style={st.modal}><View style={st.modalH}><Text style={st.modalTitle}>{selected?.name||selected?.title||selected?.contact_name||selected?.candidate_name||'Details'}</Text>
        <TouchableOpacity style={st.modalClose} onPress={()=>setShowModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity></View>
      {selected&&<ScrollView style={{flex:1,padding:16}}>
        <View style={st.mSec}><Text style={st.mSecTitle}>Details</Text>
          {Object.entries(selected).filter(([k,v])=>typeof v!=='object'&&v!=null&&!['id'].includes(k)).slice(0,15).map(([k,v],i)=>(
            <Text key={i} style={st.mTxt}>{k.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}: {String(v).substring(0,120)}</Text>
          ))}</View>
        {companyProfile&&<View style={st.mSec}><Text style={st.mSecTitle}>Company</Text><Text style={st.mTxt}>{companyProfile.name}{companyProfile.industry?` · ${companyProfile.industry}`:''}</Text>
          {companyProfile.headquarters&&<Text style={st.mTxt}>{companyProfile.headquarters}</Text>}</View>}
      </ScrollView>}</View>
    </Modal>
  );

  if(isLoading)return <SafeAreaView style={st.loading}><StatusBar barStyle="dark-content"/><ActivityIndicator size="large" color={PRIMARY}/><Text style={st.loadingTxt}>Loading...</Text></SafeAreaView>;
  return (
    <SafeAreaView style={st.container}><StatusBar barStyle="light-content"/>
      {renderHeader()}{renderStats()}{renderTabs()}{renderContent()}{renderModal()}
    </SafeAreaView>
  );
}

const st=StyleSheet.create({
  container:{flex:1,backgroundColor:'#F3F4F6'},loading:{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F3F4F6'},loadingTxt:{fontSize:16,color:'#6B7280',marginTop:16},
  header:{paddingTop:8,paddingBottom:16,paddingHorizontal:16},headerRow:{flexDirection:'row',alignItems:'center'},hBtn:{width:40,height:40,borderRadius:20,backgroundColor:'rgba(255,255,255,0.12)',justifyContent:'center',alignItems:'center'},hTitle:{fontSize:20,fontWeight:'bold',color:'#FFF'},hSub:{fontSize:13,marginTop:2},
  chips:{flexDirection:'row',flexWrap:'wrap',marginTop:10,gap:6},chip:{flexDirection:'row',alignItems:'center',backgroundColor:'rgba(255,255,255,0.08)',borderRadius:12,paddingHorizontal:8,paddingVertical:4},chipTxt:{fontSize:11,marginLeft:4},
  statsBar:{flexGrow:0,flexShrink:0,backgroundColor:'#FFF',paddingVertical:12,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},statCard:{alignItems:'center',marginRight:16,minWidth:70},statVal:{fontSize:16,fontWeight:'bold'},statLbl:{fontSize:11,color:'#6B7280',marginTop:2},
  tabsRow:{backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E5E7EB'},tab:{flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:10,marginRight:4,borderRadius:20},activeTab:{backgroundColor:'#EFF6FF'},tabTxt:{fontSize:13,color:'#6B7280',marginLeft:6,fontWeight:'500'},
  section:{backgroundColor:'#FFF',margin:16,padding:16,borderRadius:12,borderWidth:1,borderColor:'#E5E7EB'},secTitle:{fontSize:17,fontWeight:'bold',color:'#1F2937',marginBottom:12},
  card:{backgroundColor:'#FFF',marginHorizontal:16,marginBottom:8,padding:14,borderRadius:10,borderWidth:1,borderColor:'#E5E7EB'},cardName:{fontSize:15,fontWeight:'600',color:'#1F2937'},cardMeta:{fontSize:12,color:'#6B7280',marginTop:1},cardVal:{fontSize:14,fontWeight:'700',color:'#10B981'},
  badge:{borderRadius:10,paddingHorizontal:8,paddingVertical:2,marginTop:4},badgeTxt:{fontSize:11,fontWeight:'600',textTransform:'capitalize'},
  empty:{alignItems:'center',padding:32},emptyTxt:{fontSize:16,color:'#6B7280',marginTop:12},addBtn:{backgroundColor:PRIMARY,paddingHorizontal:20,paddingVertical:10,borderRadius:8,marginTop:12},addBtnTxt:{color:'#FFF',fontWeight:'600'},
  avatar:{width:40,height:40,borderRadius:20,justifyContent:'center',alignItems:'center',marginRight:10},avatarTxt:{color:'#FFF',fontWeight:'bold',fontSize:14},
  searchRow:{flexDirection:'row',alignItems:'center',backgroundColor:'#F3F4F6',borderRadius:8,paddingHorizontal:10,paddingVertical:8,margin:16},searchInput:{flex:1,fontSize:14,color:'#1F2937',marginLeft:8},
  fChip:{paddingHorizontal:12,paddingVertical:6,borderRadius:16,backgroundColor:'#F3F4F6',marginRight:6,marginTop:4},fChipTxt:{fontSize:12,color:'#6B7280',fontWeight:'500'},
  grid:{flexDirection:'row',flexWrap:'wrap',justifyContent:'space-between'},gridCard:{width:Dimensions.get('window').width/2-32,backgroundColor:'#F9FAFB',borderRadius:10,padding:14,alignItems:'center',marginBottom:8},gridVal:{fontSize:18,fontWeight:'bold'},gridLbl:{fontSize:12,color:'#6B7280',marginTop:4},
  modal:{flex:1,backgroundColor:'#FFF'},modalH:{flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},modalTitle:{fontSize:18,fontWeight:'bold',color:'#1F2937',flex:1},modalClose:{width:40,height:40,borderRadius:20,backgroundColor:'#F3F4F6',justifyContent:'center',alignItems:'center'},
  mSec:{marginBottom:20},mSecTitle:{fontSize:16,fontWeight:'600',color:'#1F2937',marginBottom:8},mTxt:{fontSize:14,color:'#6B7280',lineHeight:22},
  formField:{marginBottom:16},formLbl:{fontSize:14,fontWeight:'600',color:'#1F2937',marginBottom:6},formInput:{backgroundColor:'#F3F4F6',borderRadius:8,padding:12,fontSize:14,color:'#1F2937',borderWidth:1,borderColor:'#E5E7EB'},
  submitBtn:{flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:PRIMARY,borderRadius:10,padding:14,marginTop:8},submitBtnTxt:{color:'#FFF',fontWeight:'700',fontSize:16,marginLeft:8},
  barRow:{flexDirection:'row',alignItems:'center',marginBottom:8},barLbl:{fontSize:13,color:'#1F2937',width:100},barBg:{flex:1,height:6,backgroundColor:'#E5E7EB',borderRadius:3,marginHorizontal:8,overflow:'hidden'},barFill:{height:'100%',backgroundColor:PRIMARY,borderRadius:3},barCnt:{fontSize:13,fontWeight:'600',color:'#1F2937',width:30,textAlign:'right'},
});